import { z } from "zod";
import { GAMES } from "../domain/games";
import {
  COSMETIC_TYPES,
  FLAG_KEYS,
  LEDGER_REASONS,
  PAYMENT_PROVIDERS,
  PAYMENT_STATUSES,
  PROMO_SLOTS,
  TOURNAMENT_FORMATS,
  TOURNAMENT_STATUSES,
} from "../domain/monetization";

/**
 * A campaign link/image ref that is safe to bind into an `<a href>` / `<img src>`. Only
 * http(s) (and app-relative `/…`) — REJECTS `javascript:`/`data:`/`vbscript:` so an operator
 * (or a compromised admin) cannot author a `javascript:` CTA that runs in every user's mini-app
 * origin when they tap the promo. React does not neutralize `javascript:` in an href.
 */
const safeUrlRef = z
  .string()
  .max(500)
  .refine(
    (v) => /^https?:\/\//i.test(v) || v.startsWith("/"),
    "Must be an http(s) or app-relative (/…) URL",
  );

/**
 * User-facing DTOs for the monetization / data surfaces (Wave 2 wires the
 * endpoints; these are the contracts). Every schema is LEAK-FREE — internal cuid
 * ids at most, NEVER a Telegram id, name, or username. Monetization reads return
 * `null`/404 while their feature flag is OFF; the shapes are ready regardless.
 */

// --- Feature flags ---------------------------------------------------------

/** One flag's admin/panel view. */
export const featureFlagViewSchema = z.object({
  key: z.enum(FLAG_KEYS),
  enabled: z.boolean(),
  scope: z.string().nullable(),
  updatedAt: z.string(),
});
export type FeatureFlagView = z.infer<typeof featureFlagViewSchema>;

/** The public "which flags are on" map (`FlagService.all()`), key → enabled. */
export const flagsMapSchema = z.record(z.string(), z.boolean());
export type FlagsMap = z.infer<typeof flagsMapSchema>;

/** Admin toggle body (`PUT /admin/flags/:key`, Wave 2). */
export const setFlagSchema = z.object({
  enabled: z.boolean(),
  scope: z.string().nullish(),
  config: z.unknown().optional(),
});
export type SetFlagInput = z.infer<typeof setFlagSchema>;

// --- Events ----------------------------------------------------------------

/** Validates an emit call's shape. `type` is an open non-empty string. */
export const newEventSchema = z.object({
  userId: z.string().nullish(),
  type: z.string().min(1).max(64),
  payload: z.unknown().optional(),
});
export type NewEventInput = z.infer<typeof newEventSchema>;

// --- Rating (no UI until `rating_ui`) --------------------------------------

export const ratingViewSchema = z.object({
  game: z.enum(GAMES),
  rating: z.number(),
  games: z.number().int().min(0),
});
export type RatingView = z.infer<typeof ratingViewSchema>;

// --- Economy (wallet + ledger; behind `economy`) ---------------------------

/** `GET /me/wallet` — balance only. */
export const walletViewSchema = z.object({
  balance: z.number().int(),
});
export type WalletView = z.infer<typeof walletViewSchema>;

/** One ledger line (`GET /me/ledger`). */
export const ledgerEntryViewSchema = z.object({
  id: z.string(),
  delta: z.number().int(),
  reason: z.enum(LEDGER_REASONS),
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  balanceAfter: z.number().int(),
  createdAt: z.string(),
});
export type LedgerEntryView = z.infer<typeof ledgerEntryViewSchema>;

export const ledgerPageSchema = z.object({
  items: z.array(ledgerEntryViewSchema),
  nextCursor: z.string().nullable(),
});
export type LedgerPage = z.infer<typeof ledgerPageSchema>;

// --- Payments (stubs; behind `payments`) -----------------------------------

export const paymentIntentViewSchema = z.object({
  id: z.string(),
  provider: z.enum(PAYMENT_PROVIDERS),
  amount: z.number().int(),
  currency: z.string(),
  status: z.enum(PAYMENT_STATUSES),
  createdAt: z.string(),
});
export type PaymentIntentView = z.infer<typeof paymentIntentViewSchema>;

/** `POST /payments/intent` body — start a top-up (behind `payments`; stub today). */
export const createPaymentIntentSchema = z.object({
  provider: z.enum(PAYMENT_PROVIDERS),
  amount: z.number().int().positive(),
  currency: z.string().min(1).max(16).optional(),
  sku: z.string().min(1).max(64).optional(),
});
export type CreatePaymentIntentInput = z.infer<typeof createPaymentIntentSchema>;

export const purchaseViewSchema = z.object({
  id: z.string(),
  sku: z.string(),
  tokens: z.number().int(),
  createdAt: z.string(),
});
export type PurchaseView = z.infer<typeof purchaseViewSchema>;

// --- Promos (behind `promos` + an active campaign) -------------------------

/** `GET /promos/:slot` — what the web renders (null when off/none). */
export const promoViewSchema = z.object({
  id: z.string(),
  slot: z.enum(PROMO_SLOTS),
  title: z.string().nullable(),
  body: z.string().nullable(),
  imageRef: z.string().nullable(),
  ctaText: z.string().nullable(),
  ctaHref: z.string().nullable(),
});
export type PromoView = z.infer<typeof promoViewSchema>;

// --- Cosmetics (behind `cosmetics`) ----------------------------------------

export const cosmeticItemViewSchema = z.object({
  id: z.string(),
  type: z.enum(COSMETIC_TYPES),
  name: z.string(),
  ref: z.string(),
  price: z.number().int().min(0),
  season: z.string().nullable(),
});
export type CosmeticItemView = z.infer<typeof cosmeticItemViewSchema>;

export const userCosmeticViewSchema = z.object({
  itemId: z.string(),
  equipped: z.boolean(),
  acquiredAt: z.string(),
});
export type UserCosmeticView = z.infer<typeof userCosmeticViewSchema>;

// --- Tournaments (behind `tournaments`) ------------------------------------

export const tournamentViewSchema = z.object({
  id: z.string(),
  game: z.enum(GAMES),
  name: z.string().nullable(),
  format: z.enum(TOURNAMENT_FORMATS),
  status: z.enum(TOURNAMENT_STATUSES),
  entryFee: z.number().int().min(0),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
});
export type TournamentView = z.infer<typeof tournamentViewSchema>;

export const tournamentEntryViewSchema = z.object({
  tournamentId: z.string(),
  paidTokens: z.number().int().min(0),
  seed: z.number().int().nullable(),
  placement: z.number().int().nullable(),
});
export type TournamentEntryView = z.infer<typeof tournamentEntryViewSchema>;

// --- Referral (capture now; rewards behind `referral_rewards`) -------------

/** The referrer's own summary — counts only, never who was referred. */
export const referralSummarySchema = z.object({
  referred: z.number().int().min(0),
  rewarded: z.number().int().min(0),
});
export type ReferralSummary = z.infer<typeof referralSummarySchema>;

// ===========================================================================
// Admin monetization surfaces (cookie-auth; `monetization.manage`) — Wave 2B
// ===========================================================================
// The management-side contracts the admin panel (Wave 2C) builds against. These
// carry the FULL campaign/economy/analytics shapes an operator needs (unlike the
// leak-free public `promoView`), and are only ever returned to authenticated
// admins — never to a Mini-App user.

// --- Campaigns (admin CRUD) ------------------------------------------------

/** One campaign row for the admin list/editor (full config + tallies). */
export const campaignAdminViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  slot: z.enum(PROMO_SLOTS),
  title: z.string().nullable(),
  body: z.string().nullable(),
  imageRef: z.string().nullable(),
  ctaText: z.string().nullable(),
  ctaHref: z.string().nullable(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  targeting: z.unknown().nullable(),
  priority: z.number().int(),
  enabled: z.boolean(),
  impressions: z.number().int().min(0),
  clicks: z.number().int().min(0),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CampaignAdminView = z.infer<typeof campaignAdminViewSchema>;

/** `POST /admin/campaigns` body. */
export const createCampaignSchema = z.object({
  name: z.string().min(1).max(120),
  slot: z.enum(PROMO_SLOTS),
  title: z.string().max(200).nullish(),
  body: z.string().max(2000).nullish(),
  imageRef: safeUrlRef.nullish(),
  ctaText: z.string().max(80).nullish(),
  ctaHref: safeUrlRef.nullish(),
  startsAt: z.string().datetime().nullish(),
  endsAt: z.string().datetime().nullish(),
  targeting: z.unknown().optional(),
  priority: z.number().int().min(0).max(1_000_000).optional(),
  enabled: z.boolean().optional(),
});
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

/** `PUT /admin/campaigns/:id` body — every field optional (a partial patch). */
export const updateCampaignSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  title: z.string().max(200).nullish(),
  body: z.string().max(2000).nullish(),
  imageRef: safeUrlRef.nullish(),
  ctaText: z.string().max(80).nullish(),
  ctaHref: safeUrlRef.nullish(),
  startsAt: z.string().datetime().nullish(),
  endsAt: z.string().datetime().nullish(),
  targeting: z.unknown().optional(),
  priority: z.number().int().min(0).max(1_000_000).optional(),
  enabled: z.boolean().optional(),
});
export type UpdateCampaignInput = z.infer<typeof updateCampaignSchema>;

// --- Economy (admin manual adjustment) -------------------------------------

/** `POST /admin/economy` body — a signed manual wallet adjustment → ledger. */
export const economyAdjustSchema = z.object({
  userId: z.string().min(1),
  /** Signed token delta (positive credits, negative debits); non-zero. */
  delta: z
    .number()
    .int()
    .refine((n) => n !== 0, "delta must be non-zero"),
  reason: z.enum(LEDGER_REASONS).optional(),
  note: z.string().max(500).optional(),
  /** Optional caller-supplied idempotency key (dedupes a retried adjustment). */
  idempotencyKey: z.string().min(1).max(200).optional(),
});
export type EconomyAdjustInput = z.infer<typeof economyAdjustSchema>;

/** `GET /admin/economy/:userId` — a user's wallet + recent ledger (admin view). */
export const adminWalletViewSchema = z.object({
  userId: z.string(),
  balance: z.number().int(),
  entries: z.array(ledgerEntryViewSchema),
});
export type AdminWalletView = z.infer<typeof adminWalletViewSchema>;

// --- Tournaments (admin create) --------------------------------------------

/** `POST /admin/tournaments` body. */
export const createTournamentSchema = z.object({
  game: z.enum(GAMES),
  format: z.enum(TOURNAMENT_FORMATS),
  name: z.string().min(1).max(120).nullish(),
  status: z.enum(TOURNAMENT_STATUSES).optional(),
  entryFee: z.number().int().min(0).optional(),
  startsAt: z.string().datetime().nullish(),
  endsAt: z.string().datetime().nullish(),
});
export type CreateTournamentInput = z.infer<typeof createTournamentSchema>;

// --- Cosmetics (admin create) ----------------------------------------------

/** `POST /admin/cosmetics` body. */
export const createCosmeticItemSchema = z.object({
  type: z.enum(COSMETIC_TYPES),
  name: z.string().min(1).max(120),
  ref: z.string().min(1).max(200),
  price: z.number().int().min(0).optional(),
  season: z.string().min(1).max(60).nullish(),
  enabled: z.boolean().optional(),
});
export type CreateCosmeticItemInput = z.infer<typeof createCosmeticItemSchema>;

// --- Analytics (rollup reads) ----------------------------------------------

/** One platform-wide daily metric point (admin analytics). */
export const dailyMetricViewSchema = z.object({
  day: z.string(),
  key: z.string(),
  value: z.number(),
});
export type DailyMetricView = z.infer<typeof dailyMetricViewSchema>;

/** `GET /admin/monetization/analytics` — recent daily metrics + flag snapshot. */
export const monetizationAnalyticsSchema = z.object({
  metrics: z.array(dailyMetricViewSchema),
  flags: flagsMapSchema,
});
export type MonetizationAnalytics = z.infer<typeof monetizationAnalyticsSchema>;
